import { FormMultiSelector, FormSelect, Typography, FormInput, Form } from '@ui/components';
import { OnboardingVehicleDto, LabelType } from '@interfaces/models';
import { FormLabelsPicker } from '@shared/components';
import { PhoneCodes, Masks } from '@helpers/constant';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemo, FC } from 'react';

import { EmployeeFormType } from '../../../hooks';

interface EmployeeFormProps {
  form: UseFormReturn<EmployeeFormType>;
  availableVehicles?: OnboardingVehicleDto[];
  isFormDisabled?: boolean;
  onSubmit: (data: EmployeeFormType) => void;
}

export const EmployeeForm: FC<EmployeeFormProps> = (props) => {
  const { form, onSubmit, isFormDisabled = false, availableVehicles } = props;

  const vehiclesOptions = useMemo(() => {
    return (
      availableVehicles?.map((vehicle) => ({
        label: vehicle.registrationNumber,
        value: vehicle.id!,
      })) ?? []
    );
  }, [availableVehicles]);

  const { t } = useTranslation();

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mb-8">
          <div className="grid grid-cols-2 gap-6">
            <FormInput
              componentProps={{
                hasShadow: true,
                id: 'firstName',
              }}
              placeholder={t('ONBOARDING.ADD_EMPLOYEES.NAME')}
              formControl={form.control}
              disabled={isFormDisabled}
              className="w-full"
              name="firstName"
              size="large"
              required
              floating
            />

            <FormInput
              componentProps={{
                hasShadow: true,
                id: 'lastName',
              }}
              placeholder={t('ONBOARDING.ADD_EMPLOYEES.LAST_NAME')}
              formControl={form.control}
              disabled={isFormDisabled}
              className="w-full"
              name="lastName"
              size="large"
              required
              floating
            />
          </div>

          <div className="grid grid-cols-6 gap-6">
            <FormSelect
              componentProps={{
                hasShadow: true,
              }}
              className="w-full col-span-1"
              formControl={form.control}
              disabled={isFormDisabled}
              options={PhoneCodes}
              name="phoneExt"
              size="large"
              required
            />

            <FormInput
              componentProps={{
                maskOptions: { mask: Masks.PHONE },
                id: 'phoneNumber',
                hasShadow: true,
              }}
              placeholder={t('ONBOARDING.ADD_EMPLOYEES.PHONE_NUMBER')}
              className="w-full mb-4 col-span-5"
              formControl={form.control}
              name="phoneNumber"
              size="large"
              required
              floating
            />
          </div>

          <FormInput
            componentProps={{
              hasShadow: true,
            }}
            placeholder={t('ONBOARDING.ADD_EMPLOYEES.EMAIL')}
            className="w-full col-span-4"
            formControl={form.control}
            disabled={isFormDisabled}
            name="email"
            size="large"
            floating
          />

          <FormLabelsPicker
            componentProps={{ hasShadow: true, id: 'labels' }}
            placeholder={t('ONBOARDING.ADD_EMPLOYEES.LABEL')}
            className="w-full col-span-4 mb-8"
            labelType={LabelType.EMPLOYEE}
            formControl={form.control}
            disabled={isFormDisabled}
            name="labels"
            size="large"
            floating
          />

          {availableVehicles && (
            <>
              <Typography variant="h200">
                {t('ONBOARDING.ADD_EMPLOYEES.ASSIGN_VEHICLES_TITLE')}
              </Typography>

              <Typography>{t('ONBOARDING.ADD_EMPLOYEES.ASSIGN_VEHICLES_DESCRIPTION')}</Typography>
              <FormMultiSelector
                componentProps={{
                  emptyContent: (
                    <Typography variant="labelMediumBook" className="text-disabled">
                      {t('ONBOARDING.ADD_EMPLOYEES.NO_VEHICLES_TO_ASSIGN')}
                    </Typography>
                  ),
                  hasShadow: true,
                }}
                placeholder={t('ONBOARDING.ADD_EMPLOYEES.ASSIGN_VEHICLES')}
                formControl={form.control}
                options={vehiclesOptions}
                name="vehicles"
                size="large"
                floating
              />
            </>
          )}
        </form>
      </Form>
    </div>
  );
};
